import React from 'react';

// --- Komponen ErrorBoundary (Tidak Berubah) ---
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }
    return this.props.children;
  }
}

// --- Komponen Fallback (Desain Baru) ---

// Ikon SVG minimalis untuk menggantikan Font Awesome
const ErrorIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    fill="currentColor"
    className="bi bi-exclamation-triangle text-danger mb-4"
    viewBox="0 0 16 16"
  >
    <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016l6.605 11.667c.04.072.019.166-.043.195l-.104.054c-.062.032-.143.01-.184-.049L8 3.666 1.626 13.884c-.04.06-.122.08-.184.049l-.104-.054c-.062-.029-.083-.123-.043-.195L7.938 2.016zM8 1.5a.5.5 0 0 0-.434.248L.866 13.416a.5.5 0 0 0 .434.749h13.398a.5.5 0 0 0 .434-.749L8.434 1.748A.5.5 0 0 0 8 1.5z" />
    <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z" />
  </svg>
);

const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({ error, resetError }) => (
  <div className="vh-100 d-flex align-items-center justify-content-center bg-light-subtle">
    <div className="bg-white p-5 rounded-4 shadow-sm text-center" style={{ maxWidth: '500px' }}>
      <ErrorIcon />
      <h2 className="h3 fw-light text-dark mb-3">Terjadi Kesalahan</h2>
      <p className="text-muted mb-4">
        Maaf, terjadi kesalahan pada aplikasi. Silakan muat ulang untuk mencoba lagi.
      </p>

      {process.env.NODE_ENV === 'development' && error && (
        <details className="text-start mb-4">
          <summary className="btn btn-link text-muted btn-sm text-decoration-none">
            Lihat Detail Error (Dev)
          </summary>
          <pre
            className="mt-2 p-3 bg-dark text-light rounded-3 small"
            style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
          >
            {error.toString()}
          </pre>
        </details>
      )}

      <button
        onClick={resetError}
        className="btn btn-outline-primary rounded-pill px-4"
      >
        Muat Ulang
      </button>
    </div>
  </div>
);

export default ErrorBoundary;
